import {closeDialog, openDialog} from 'platform/components';

import {
  usePostServiceItemsMaterialPartRequestCancellationMutation,
  usePostServiceItemsMaterialPartRequestMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {type GridDataChange} from 'features/eag-grid';

import {IssueMaterialItems} from '../components/IssueMaterialItems';
import {getDefaultMechanicId} from '../utils/getDefaultMechanicId';
import {getWorkItemRowsWithAction} from '../utils/getWorkItemRowsWithAction';
import {mapRowsToServiceItems} from '../utils/mapRowsToServiceItems';

const ISSUE_ACTION_KEY = 'issue';
const REQUEST_ACTION_KEY = 'request';
const REQUEST_CANCELLATION_ACTION_KEY = 'requestCancellation';
const ISSUE_DIALOG_ID = 'issueMaterialItem';

type UseOrderItemsBulkActionsParams = TestIdProps & {
  gridData: GridDataChange | null;
  serviceCaseId: string;
  serviceOrderId: string;
  /**
   * Runs after a successful bulk mutation / issue-dialog submit. The host owns the
   * refresh so it can mirror its per-row handlers (OrderItemsDataGrid refetches the
   * service case + orders before refreshing the grid).
   */
  onActionSuccess: () => void;
};

/**
 * "More actions" bulk operations over an order's All items grid (T20-138767): issue
 * material, request material, and request cancellation, each applied to EVERY loaded
 * row whose backend actions expose the corresponding key (the same applicability signal
 * the grid uses per row). Counts feed the dropdown labels; rows come from
 * onGridDataChange. Unlike the job-scoped twin (useWorkItemsBulkActions), rows span
 * multiple jobs, so each payload item's serviceJobId is read from the row's own cell.
 *
 * Gating is strictly per action, never menu-wide: each mutation's in-flight state
 * disables (and spins) only its own item, so running one action leaves the others
 * usable. Per-item disabling is also what protects against double-submit, since an
 * already-open Dropdown stays interactive even when its trigger is disabled.
 */
export const useOrderItemsBulkActions = (params: UseOrderItemsBulkActionsParams) => {
  const [postServiceItemsMaterialPartRequest, requestMaterialState] =
    usePostServiceItemsMaterialPartRequestMutation();
  const [postServiceItemsMaterialPartRequestCancellation, requestCancellationState] =
    usePostServiceItemsMaterialPartRequestCancellationMutation();

  const rows = params.gridData?.rows ?? [];
  const issueRows = getWorkItemRowsWithAction(rows, ISSUE_ACTION_KEY);
  const requestRows = getWorkItemRowsWithAction(rows, REQUEST_ACTION_KEY);
  const cancellationRows = getWorkItemRowsWithAction(rows, REQUEST_CANCELLATION_ACTION_KEY);

  const issueMaterial = () =>
    openDialog(
      <IssueMaterialItems
        serviceCaseId={params.serviceCaseId}
        serviceOrderId={params.serviceOrderId}
        items={mapRowsToServiceItems(issueRows)}
        defaultMechanicId={getDefaultMechanicId(issueRows)}
        onSubmit={params.onActionSuccess}
        onClose={() => closeDialog(ISSUE_DIALOG_ID)}
        data-testid={suffixTestId(ISSUE_DIALOG_ID, params)}
      />,
      {
        title: i18n.t('entity.orderRequest.labels.issueMaterialItem'),
        id: ISSUE_DIALOG_ID,
        size: 'small',
      }
    );

  const requestMaterial = () =>
    postServiceItemsMaterialPartRequest({
      body: {serviceItems: mapRowsToServiceItems(requestRows)},
      serviceCaseId: params.serviceCaseId,
      serviceOrderId: params.serviceOrderId,
    })
      .unwrap()
      .then(params.onActionSuccess)
      .catch(handleApiError);

  const requestCancellation = () =>
    postServiceItemsMaterialPartRequestCancellation({
      body: {serviceItems: mapRowsToServiceItems(cancellationRows)},
      serviceCaseId: params.serviceCaseId,
      serviceOrderId: params.serviceOrderId,
    })
      .unwrap()
      .then(params.onActionSuccess)
      .catch(handleApiError);

  return {
    /** In-flight state of each mutation — gates (and spins) only its own item. */
    isRequestMaterialPending: requestMaterialState.isLoading,
    isRequestCancellationPending: requestCancellationState.isLoading,
    issueCount: issueRows.length,
    requestCount: requestRows.length,
    cancellationCount: cancellationRows.length,
    issueMaterial,
    requestMaterial,
    requestCancellation,
  };
};
